import React, {useEffect} from "react";
import { Typography } from "@material-ui/core";
import { useDispatch } from "react-redux";
import { KDialog, KList, KLoader } from "@kyburz/kdesign-system";
import { loadFirmwareToDevices } from "../../../../apiCalls/config/firmware";
import { loadConfigToDevices } from "../../../../apiCalls/config/config";
import { setAlert } from "../../../../redux/actions/actions";
import KSelectInfinite from "../../../../components/ui/KSelectInfinite/KSelectInfinite";
import { useStyles } from "./styles";
import useSetState from "../../../../hooks/useSetState";
import { UpdateElements, UpdateType } from "../types";
import { fetchFirmwareOrConfigList } from "../useFwOrConfigList";


interface Props {
  updateModalOpen: boolean;
  selectedImeis: string[];
  toggleUpdatePopup: () => void;
  selectedHardwareTypes: Set<string>;
  selectedSimTypes: Set<string | undefined>
  onSuccess: () => void;
  startLoading: () => void;
  updateType: UpdateType;
}

const UpdatePopup = ({
  updateModalOpen,
  selectedImeis,
  toggleUpdatePopup,
  selectedHardwareTypes,
  selectedSimTypes,
  onSuccess,
  startLoading,
  updateType,
}: Props) => {
  const dispatch = useDispatch();
  const isFirmware: boolean = updateType === UpdateType.firmware;
  const classes = useStyles();

  const [
    {
      firmwareOrConfigList,
      loading,
      deviceFirmwareOrConfigID,
      page,
      hasNextPage,
    },
    setState,
  ] = useSetState({
    firmwareOrConfigList: [] as UpdateElements[],
    loading: true,
    deviceFirmwareOrConfigID: undefined as string | undefined,
    page: 1,
    hasNextPage: false,
  });

  useEffect(() => {
    const promise = fetchFirmwareOrConfigList({
      updateType: updateType as UpdateType,
      hardwareTypes: selectedHardwareTypes,
      simTypes: selectedSimTypes,
      extraFilters: ["filter_no_imei=1"],
      page: 1,
    });
    if (!promise) return;

    setState({ loading: true, firmwareOrConfigList: [], page: 1, hasNextPage: false });
    promise.then(({ data, hasNextPage: nextHasNextPage }) => {
      setState({ firmwareOrConfigList: data, loading: false, hasNextPage: nextHasNextPage, page: 1 });
    });
  }, [updateType]);

  const loadNextPage = () => {
    if (!hasNextPage || loading) return;

    const nextPage = page + 1;
    const promise = fetchFirmwareOrConfigList({
      updateType,
      hardwareTypes: selectedHardwareTypes,
      simTypes: selectedSimTypes,
      extraFilters: ["filter_no_imei=1"],
      page: nextPage,
    });
    if (!promise) return;

    setState({ loading: true });
    promise.then(({ data, hasNextPage: nextHasNextPage }) => {
      setState({
        firmwareOrConfigList: [...firmwareOrConfigList, ...data],
        hasNextPage: nextHasNextPage,
        page: nextPage,
        loading: false,
      });
    });
  };

  const handleUpdate = () => {
    startLoading();
    const req = isFirmware
      ? loadFirmwareToDevices(selectedImeis, deviceFirmwareOrConfigID)
      : loadConfigToDevices(selectedImeis, deviceFirmwareOrConfigID);
    req.then((res) => {
      if (res?.data?.status === 200) {
        toggleUpdatePopup();
        const alert = isFirmware
          ? "Started firmware upload successfully"
          : "Started config upload successfully";
        dispatch(setAlert(alert, "success"));
        onSuccess();
      } else {
        dispatch(setAlert(res.detail || "Error starting upload", "error"));
        onSuccess();
      }
    });
  };

  // hardware types (for firmwares) or both hardware and sim type (for configs) should match
  // otherwise update is not possible
  const firmwareOrConfigListFiltered =
    firmwareOrConfigList.filter(
      (y) =>
        selectedHardwareTypes.has(y.hardwareType?.toLowerCase()) &&
        (isFirmware || selectedSimTypes.has(y.simType?.toLowerCase())),
    ) || [];

  const handleFwOrConfigSelect = (element: { label: string; id: string }) => {
    setState({ deviceFirmwareOrConfigID: element?.id });
  };

  const initialLoading = loading && firmwareOrConfigList.length === 0;

  return (
    <KDialog
      size="sm"
      title={`Update ${isFirmware ? "Firmware" : "Config"}`}
      open={updateModalOpen}
      primaryCTAonClick={handleUpdate}
      primaryCTAColor="primary"
      primaryCTAText={`Update ${selectedImeis.length} devices`}
      secondaryCTAonClick={toggleUpdatePopup}
      secondaryCTAText="Cancel"
      hasOverflow
      disabled={!deviceFirmwareOrConfigID || initialLoading}
    >
      <div className={classes.container}>
        <div className={classes.dropdownFWSelector}>
          {initialLoading ? (
            <KLoader height="50px" />
          ) : (
            <KSelectInfinite
              placeholder={`Select ${isFirmware ? "Firmware" : "Config"}`}
              elements={firmwareOrConfigListFiltered}
              onSelect={handleFwOrConfigSelect}
              style={{ width: "100%" }}
              value={
                firmwareOrConfigListFiltered.find(
                  (x) => x.id.toString() === deviceFirmwareOrConfigID?.toString()
                )?.label ?? ""
              }
              hasNextPage={hasNextPage}
              loadNextPage={loadNextPage}
            />
          )}
        </div>
          <div className={classes.divider} />
          <Typography variant="body1" color="textSecondary">
            Devices to update:
          </Typography>
        <div className={classes.listContainer}>

          <KList
            elements={
              selectedImeis[0] === "all"
                ? [
                  "Not possible to select all IMEIs for now. Please filter by customer or select IMEIs manually",
                ]
                : selectedImeis
            }
            height={100}
            selectable={false}
            width="auto"
          />
        </div>
      </div>
    </KDialog>
  );
};

export default UpdatePopup;